/**
 * API Client
 * Базовый клиент для работы с MarkApp API
 * ⚠️ SECURITY UPDATE: Автоматическое обновление токенов (Refresh Token механизм)
 */

import axios from 'axios';
import { setOffline } from '../utils/networkStatus';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Флаг для предотвращения множественных refresh запросов
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Interceptor для добавления токена
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Interceptor для обработки ошибок и автоматического refresh
api.interceptors.response.use(
  response => {
    setOffline(false);
    return response;
  },
  async error => {
    if (error.code === 'ERR_CANCELED') {
      setOffline(false);
      return Promise.reject(error);
    }
    const originalRequest = error.config;
    const status = error.response?.status;
    const isNetworkIssue = !error.response || (status >= 500 && status < 600);

    if (isNetworkIssue) {
      setOffline(true);
    } else {
      setOffline(false);
    }
    
    // Если 401 ошибка и это не запрос refresh
    if (status === 401 && originalRequest && !originalRequest._retry) {
      // Предотвращаем повторные попытки для того же запроса
      if (originalRequest.url.includes('/auth/telegram/refresh')) {
        // Если refresh endpoint вернул 401, значит refresh token тоже недействителен
        console.error('❌ Refresh token недействителен, требуется повторная авторизация');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        // Если уже идет refresh, добавляем запрос в очередь
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        console.error('❌ Refresh token отсутствует, требуется авторизация');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      window.location.href = '/login';
        return Promise.reject(error);
      }
      
      try {
        
        
        // Отправляем запрос на обновление токена
        const response = await axios.post(
          `${API_BASE_URL}/auth/telegram/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );
        
        const { access_token, refresh_token: new_refresh_token } = response.data;
        
        // Сохраняем новые токены
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', new_refresh_token);
        
        
        
        // Обновляем заголовок Authorization для оригинального запроса
        originalRequest.headers['Authorization'] = 'Bearer ' + access_token;
        
        // Обрабатываем очередь запросов
        processQueue(null, access_token);
        
        isRefreshing = false;
        
        // Повторяем оригинальный запрос
        return axios(originalRequest);
        
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        
        console.error('❌ Ошибка обновления токенов:', refreshError);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
